import { useState } from 'react';
import type { CSSProperties } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { DS2 } from '../theme/designTokens';
import { AuthPageShell } from '../components/ui/AuthPageShell';
import { AuthBrandMark } from '../components/ui/AuthBrandMark';
import { AuthField } from '../components/ui/AuthField';
import { AuthSubmitButton } from '../components/ui/AuthSubmitButton';
import { signIn, signInWithGoogle } from '../utils/firebase';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../i18n/react';
import { ASSETS } from '../config/assets';

// Figma glow geometry for this frame ×0.75 — shared by ForgotPasswordScreen's
// email-entry step, which specifies the identical values.
export const LOGIN_GLOW_1 = { width: 1167, height: 877.5, top: 210.75, leftOffset: 465, radius: 164.25 };
export const LOGIN_GLOW_2 = { width: 1207.5, height: 935.25, top: -213.75, leftOffset: -237.75, radius: 0 };

/**
 * LOGIN — Figma "LOG IN" frame, 2560×1321 export, values ×0.75 (established
 * desktop scale, matches GetStartedScreen). Lives at /login inside the DS2
 * shell (Sidebar on desktop, DS2 top bar on mobile) — the sidebar itself is
 * the shared shell chrome, not reproduced locally here.
 *
 * Login is the calibration screen for the auth family (Signup, Forgot
 * Password remain on the legacy AuthShellV2 presentation until corrected
 * tokens from this screen's QA pass are applied to them).
 */

// Figma 363×52 buttons ×0.75 → 272.25×39; padding 10/16, gap 12, font 22 scaled.
const AUTH_BUTTON_BASE: CSSProperties = {
  boxSizing:      'border-box',
  display:        'flex',
  flexDirection:  'row',
  alignItems:     'center',
  justifyContent: 'center',
  gap:            9,
  width:          272.25,
  height:         39,
  padding:        '7.5px 12px',
  fontFamily:     DS2.font.body,
  fontWeight:     500,
  fontSize:       16.5,
  lineHeight:     '18px',
  border:         '0.75px solid #C9A84C',
  cursor:         'pointer',
  WebkitTapHighlightColor: 'transparent',
};

function AuthIcon({ src, size }: { src: string; size: number }) {
  return (
    <img
      src={src}
      alt=""
      style={{ width: size, height: size, objectFit: 'contain', flexShrink: 0 }}
      draggable={false}
    />
  );
}

function GhostAuthButton({ children, onClick, disabled }: { children: React.ReactNode; onClick?: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        ...AUTH_BUTTON_BASE,
        background:   '#131313',
        borderRadius: 12,
        color:        '#F5F5F5',
        cursor:       disabled ? 'not-allowed' : (onClick ? 'pointer' : 'default'),
        opacity:      disabled ? 0.6 : 1,
      }}
    >
      {children}
    </button>
  );
}

export function LoginScreen() {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const { loginWithDevToken } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  // Error state holds a translation KEY (not a rendered string) so an
  // already-visible error re-renders in the new language on a switch.
  const [error,    setError]    = useState<'login.errorInvalid' | 'login.errorGeneric' | ''>('');
  const [loading,  setLoading]  = useState(false);
  // Apple/Facebook sign-in are hidden for now (product decision) — only
  // Google and email are offered. firebase.ts still exports
  // signInWithApple/signInWithFacebook for a future re-enable; nothing
  // here calls them.
  const [socialLoading, setSocialLoading] = useState<'google' | null>(null);

  // Maps known local test-user emails to their backend DEV_TOKEN.
  // Only consulted when import.meta.env.DEV is true — tree-shaken in prod builds.
  const DEV_EMAIL_TOKENS: Record<string, string> = {
    'testuser@thedominogame.com':  'DEV_TOKEN',
    'testuser1@thedominogame.com': 'DEV_TOKEN',
    'testuser2@thedominogame.com': 'DEV_TOKEN_2',
    'testuser3@thedominogame.com': 'DEV_TOKEN_3',
    'testuser4@thedominogame.com': 'DEV_TOKEN_4',
  };

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    // ── Local dev bypass ──────────────────────────────────────────────────
    // Only active in Vite dev mode (import.meta.env.DEV = false in all prod
    // builds). Recognised test emails map to the backend DEV_TOKEN bypass —
    // no Firebase call.
    if (import.meta.env.DEV) {
      const token = DEV_EMAIL_TOKENS[username.toLowerCase().trim()];
      if (token) {
        console.log('[LoginScreen] Using local dev auth for', username, '→', token);
        loginWithDevToken(token, username);
        setLoading(false);
        navigate('/home', { replace: true });
        return;
      }
    }

    // ── Firebase auth (all other cases, always used in production) ─────────
    console.log('[LoginScreen] Using Firebase auth for', username);
    try {
      await signIn(username, password);
      navigate('/home', { replace: true });
    } catch (err: unknown) {
      const fe = err as { code?: string; message?: string };
      console.error('[LoginScreen] Firebase error — code:', fe.code, '| message:', fe.message);
      setError('login.errorInvalid');
    } finally {
      setLoading(false);
    }
  }

  async function handleSocialSignIn(provider: 'google') {
    setError('');
    setSocialLoading(provider);
    try {
      await signInWithGoogle();
      navigate('/home', { replace: true });
    } catch (err: unknown) {
      const fe = err as { code?: string; message?: string };
      console.error('[LoginScreen] Social sign-in error — code:', fe.code, '| message:', fe.message);
      if (fe.code !== 'auth/popup-closed-by-user' && fe.code !== 'auth/cancelled-popup-request') {
        setError('login.errorGeneric');
      }
    } finally {
      setSocialLoading(null);
    }
  }

  return (
    <AuthPageShell glow1={LOGIN_GLOW_1} glow2={LOGIN_GLOW_2}>
        <AuthBrandMark />

        {/* Welcome Back! — Montserrat 700 32.44/39 ×0.75, letter-spacing -0.48px */}
        <h1
          style={{
            margin:        '18px 0 0',
            fontFamily:    DS2.font.heading,
            fontWeight:    700,
            fontSize:      24.33,
            lineHeight:    '29.25px',
            letterSpacing: '-0.48px',
            textAlign:     'center',
            color:         '#FFFFFF',
          }}
        >
          {t('login.heading')}
        </h1>

        <form onSubmit={handleLogin} style={{ width: '100%', marginTop: 24, display: 'flex', flexDirection: 'column', gap: 15 }}>
          <AuthField label={t('login.email')} value={username} onChange={setUsername} autoComplete="username" />
          <AuthField label={t('login.password')} value={password} onChange={setPassword} type="password" autoComplete="current-password" />

          {error && (
            <p style={{ margin: 0, fontFamily: DS2.font.body, fontSize: 12.6, color: '#f87171', textAlign: 'center' }}>
              {t(error)}
            </p>
          )}

          {/* Log In — gold gradient submit, Figma Group 46 (363×52 ×0.75) */}
          <div style={{ marginTop: 3 }}>
            <AuthSubmitButton loading={loading} loadingLabel={t('login.loggingIn')}>{t('login.submit')}</AuthSubmitButton>
          </div>
        </form>

        {/* I forgot my password — Roboto 700 19.2/29 ×0.75, underlined */}
        <Link
          to="/forgot-password"
          style={{
            marginTop:      18,
            fontFamily:     DS2.font.body,
            fontWeight:     700,
            fontSize:       14.4,
            lineHeight:     '21.75px',
            textAlign:      'center',
            color:          '#FFFFFF',
            textDecoration: 'underline',
          }}
        >
          {t('login.forgotLink')}
        </Link>

        {/* or — Figma Frame 79: text flanked by two hairlines */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', marginTop: 27 }}>
          <div style={{ flex: 1, height: 0.9, background: '#FFFFFF' }} />
          <span style={{ fontFamily: DS2.font.body, fontWeight: 700, fontSize: 14.4, color: '#FFFFFF' }}>{t('login.or')}</span>
          <div style={{ flex: 1, height: 0.9, background: '#FFFFFF' }} />
        </div>

        {/* Provider buttons — Google only for now (Apple/Facebook hidden,
            product decision); email sign-in is the form above. */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 15, marginTop: 27, alignItems: 'center', width: '100%' }}>
          <GhostAuthButton onClick={() => handleSocialSignIn('google')} disabled={!!socialLoading || loading}>
            <AuthIcon src={ASSETS.auth.GOOGLE} size={11.6} />
            {socialLoading === 'google' ? t('login.signingIn') : t('login.withGoogle')}
          </GhostAuthButton>
        </div>
    </AuthPageShell>
  );
}
